import { ColumnDef, ColumnDefResolved } from '@tanstack/react-table';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Clip } from '../types';
import { ClipTitleCell } from '../components/ClipTitleCell';
import { FormattedDate } from '@common/components/FormattedDate';
import { FormattedVolume } from '@common/components/FormattedVolume';
import { MediaObjectTypeBadge } from '../components/MediaObjectTypeBadge';

export const useClipColumns = () => {
    const { t } = useTranslation('clipDetails');

    const columns: ColumnDef<Clip>[] = useMemo(
        () =>
            (
                [
                    {
                        accessorKey: 'id',
                        size: 60,
                        minSize: 60,
                        maxSize: 60,
                        meta: {
                            defaultHidden: true,
                        },
                    },
                    {
                        accessorKey: 'clipName',
                        cell: ({ row: { original } }) => (
                            <ClipTitleCell clip={original} />
                        ),
                        size: 250,
                        maxSize: 400,
                        enableResizing: true,
                    },
                    {
                        accessorKey: 'episode',
                        size: 100,
                    },
                    {
                        accessorKey: 'mediaObjectType',
                        cell: ({ row: { original } }) => (
                            <MediaObjectTypeBadge clip={original} />
                        ),
                    },
                    {
                        accessorKey: 'created',
                        cell: ({ cell }) => {
                            const date = cell.getValue() as number;
                            return <FormattedDate date={date} />;
                        },
                        size: 70,
                        meta: {
                            defaultHidden: true,
                        },
                    },
                    {
                        accessorKey: 'updated',
                        cell: ({ cell }) => {
                            const date = cell.getValue() as number;
                            return <FormattedDate date={date} />;
                        },
                        size: 85,
                        meta: {
                            defaultHidden: true,
                        },
                    },
                    {
                        accessorKey: 'commercialOffer',
                        size: 100,
                    },
                    {
                        accessorKey: 'preservationCategory',
                        size: 100,
                    },
                    {
                        accessorKey: 'customer',
                        size: 100,
                        meta: {
                            defaultHidden: true,
                        },
                    },
                    {
                        accessorKey: 'container',
                        size: 100,
                    },
                    {
                        accessorKey: 'codec',
                        size: 100,
                    },
                    {
                        accessorKey: 'size',
                        size: 70,
                        cell: ({ cell }) => {
                            const volume = cell.getValue() as number;
                            return <FormattedVolume volume={volume} />;
                        },
                    },
                ] as ColumnDefResolved<Clip>[]
            ).map((item) => ({
                ...item,
                header: t(`metadata.${item.accessorKey}`, {
                    ns: 'clipDetails',
                }),
            })),
        [t],
    );

    return columns;
};
